<template>
  <v-row>
    <v-col cols="12">
      <!-- ELECTRO WORKFLOW -->
      <v-card
        v-if="genericProduct === false"
        class="rounded-xl mt-4 d-flex-column global-card"
        :border="false"
        :elevation="0"
      >
        <!--SEARCH FIELD-->
        <v-row>
          <v-col md="8"
          ><h1>{{ $t('FILE_STEP_PRODUCT.TITLE') }}</h1></v-col
          >
          <v-col md="4">
            <v-text-field
              append-inner-icon="mdi-magnify"
              v-model.trim="search"
              v-model="search"
              :placeholder="$t('COMMON.FILTER.SEARCH')"
              hide-details
            />
          </v-col>
        </v-row>
        <!--FILTERS-->
        <v-row class="mt-4">
          <!--MANUFACTURER-->
          <v-col cols="12" md="4">
            <p class="mb-4">{{ $t('FILE_STEP_PRODUCT.SELECT.MANUFACTURER') }}</p>
            <v-autocomplete
              id="select-manufacturer"
              v-model="selectedManufacturer"
              :items="manufacturerList"
              :no-data-text="$t('COMMON.NO_DATA')"
              :placeholder="$t('FILE_STEP_PRODUCT.PLACEHOLDER.MANUFACTURER')"
              hide-details
              clearable
              item-title="name"
              item-value="technical_id"
              :disabled="selectedCategory"
            >
              <template v-slot:selection="{ item }">
                <v-chip size="small">
                  <span>{{ item.title }}</span>
                </v-chip>
              </template>
              <template v-slot:item="{ item, props }">
                <v-list-item :title="item.title" :value="item.value" v-bind="props">
                  <v-tooltip
                    v-if="shouldShowTooltip(item, 'select-manufacturer')"
                    activator="parent"
                    location="bottom left"
                  >
                    {{ item.title }}
                  </v-tooltip>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>

          <!--CATEGORY-->
          <v-col cols="12" md="4">
            <p class="mb-4">{{ $t('FILE_STEP_PRODUCT.SELECT.CATEGORY') }}</p>
            <v-autocomplete
              id="select-category"
              v-model="selectedCategory"
              :items="categoryList"
              :no-data-text="$t('COMMON.NO_DATA')"
              :placeholder="$t('FILE_STEP_PRODUCT.PLACEHOLDER.CATEGORY')"
              hide-details
              clearable
              item-title="name"
              item-value="technical_id"
              :disabled="!selectedManufacturer || selectedSubCategory.length > 0"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip size="small" v-if="index < 1">
                  <span>{{ item.title }}</span>
                </v-chip>
              </template>
              <template v-slot:item="{ item, props }">
                <v-list-item :title="item.title" :value="item.value" v-bind="props">
                  <v-tooltip
                    v-if="shouldShowTooltip(item, 'select-category')"
                    activator="parent"
                    location="bottom left"
                  >
                    {{ item.title }}
                  </v-tooltip>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>

          <!--SUB CATEGORY-->
          <v-col cols="12" md="4">
            <p class="mb-4">{{ $t('FILE_STEP_PRODUCT.SELECT.SUB_CATEGORY') }}</p>
            <v-autocomplete
              id="select-sub-category"
              v-model="selectedSubCategory"
              :items="subCategoryList"
              multiple
              :no-data-text="$t('COMMON.NO_DATA')"
              :placeholder="$t('FILE_STEP_PRODUCT.PLACEHOLDER.SUB_CATEGORY')"
              hide-details
              clearable
              item-title="name"
              item-value="technical_id"
              :disabled="!selectedCategory"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip size="small" v-if="index < 1">
                  <span>{{ item.title }}</span>
                </v-chip>
                <span v-if="index === 1" class="text-grey text-caption align-self-center">
                  (+{{ selectedSubCategory.length - 1 }}
                  {{
                    selectedSubCategory.length - 1 === 1
                      ? $t('COMMON.TABLE.OTHER')
                      : $t('COMMON.TABLE.OTHERS')
                  }})
                </span>
              </template>
              <template v-slot:item="{ item, props }">
                <v-list-item :title="item.title" :value="item.value" v-bind="props">
                  <v-tooltip
                    v-if="shouldShowTooltip(item, 'select-sub-category')"
                    activator="parent"
                    location="bottom left"
                  >
                    {{ item.title }}
                  </v-tooltip>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="12">
            <div class="d-flex flex-column flex-md-row justify-space-between">
              <p class="mt-1">{{ $t('FILE_STEP_PRODUCT.LABEL.PRODUCT') }}</p>
              <p class="mt-1" style="color: #acacac">
                {{ $t('FILE_STEP_PRODUCT.LABEL.PRODUCT_POPULAR') }}
              </p>
            </div>
            <v-infinite-scroll
              class="class-infinite mt-2 pb-2"
              v-if="products.length"
              @scroll="onScroll"
              direction="horizontal"
              @load="load"
              empty-text=""
            >
              <template v-for="(product, index) in products" :key="product.id">
                <v-hover>
                  <template v-slot:default="{ isHovering, props }">
                    <v-card
                      :ripple="false"
                      :id="product.id"
                      @click="openDialog(index); selectProduct(index)"
                      v-bind="props"
                      :class="[
                        'product-card',
                        { 'unrepairable-card': product.reparable === 'inactive' },
                        { 'selected-card': product.selected }
                      ]"
                    >
                      <v-dialog v-model="dialogStates[index]" width="50%">
                        <template v-slot:activator="{ props }">
                          <div v-bind="props" class="picture">
                            <v-col class="pa-0" v-if="product.picture">
                              <v-img
                                class="picture-content"
                                :src="product.picture"
                                alt="Product Preview"
                              />
                            </v-col>
                          </div>
                        </template>
                        <template v-slot>
                          <v-card>
                            <div v-bind="props" class="dialog-picture">
                              <v-col class="pa-0" v-if="product.picture">
                                <v-img
                                  class="dialog-picture-content"
                                  :src="product.picture"
                                  alt="Product Preview"
                                />
                              </v-col>
                            </div>
                            <v-card-actions>
                              <button class="dialog-close-btn" @click="closeDialog(index)">
                                <img
                                  alt="dialog-icon"
                                  class="dialog-img"
                                  src="@/assets/icons/file/cross.svg"
                                />
                              </button>
                            </v-card-actions>
                          </v-card>
                        </template>
                      </v-dialog>

                      <v-card-item></v-card-item>

                      <v-card-text
                        class="mx-0 mt-15 d-flex align-center justify-center flex-column"
                        style="
                          display: flex;
                          flex-direction: column;
                          justify-content: center;
                          align-items: center;
                          gap: 10px;
                          align-self: stretch;
                        "
                        v-if="isHovering && product.reparable === 'inactive'"
                      >
                        <img
                          src="@/assets/icons/file/info.svg"
                          alt="info-icon"
                          class="mb-2"
                          style="width: 14px; height: 14px"
                        />

                        <p class="product-name">{{ $t('FILE_STEP_PRODUCT.LABEL.UNSELECTABLE') }}</p>
                      </v-card-text>
                      <v-card-text v-else>
                        <v-row
                          align="center"
                          class="mx-0 flex-fill d-flex align-center justify-center flex-column"
                        >
                          <p class="manufacturer-name mt-5 mb-1">
                            {{ capitalizeFirstLetter(product.manufacturer.name) }}
                          </p>
                          <p v-if="product.reparable === 'inactive'" class="product-name-inactive">
                            {{ product.boomerang_name }}
                          </p>
                          <p v-else class="product-name">{{ product.boomerang_name }}</p>
                          <p
                            v-if="product.reparable === 'inactive'"
                            class="product-description text-overflow-ellipsis"
                          >
                            {{ product.description }}
                          </p>
                          <p v-else class="product-name text-overflow-ellipsis">
                            {{ product.description }}
                          </p>
                          <v-col
                            v-if="product.reparable === 'inactive'"
                            class="d-flex align-center mt-5 pa-0"
                          >
                            <span class="status-dot mr-2" />
                            <p class="product-name pr-2">
                              {{ $t('FILE_STEP_PRODUCT.LABEL.NO_REPARABLE') }}
                            </p>
                            <img src="@/assets/icons/file/cross-red.svg" alt="cross-icon" />
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </template>
                </v-hover>
              </template>
            </v-infinite-scroll>
            <tr v-else-if="!products.length" class="d-flex justify-center" style="padding: 69px">
              <td class="text-md-center">
                {{ $t('FILE_STEP_PRODUCT.PLACEHOLDER.NO_PRODUCT') }}
                <div class="d-flex align-center justify-center">
                  <img src="@/assets/images/empty.svg" alt="Empty" class="empty-image" />
                </div>
              </td>
            </tr>
          </v-col>
        </v-row>
        <div style="display: flex; justify-content: end">
          <button
            v-if="addProduct === false"
            class="add-product-question"
            @click="addProduct = true"
          >
            {{ $t('FILE_STEP_PRODUCT.BUTTON.ADD_PRODUCT_QUESTION') }}
          </button>
          <v-btn
            v-else
            class="boom-button-transparent mt-4"
            variant="outlined"
            @click="setGenericProduct(true)"
          >
            <img
              alt="cross_round-icon"
              class="cross-svg mr-2"
              src="@/assets/icons/file/cross_round.svg"
            />
            {{ $t('FILE_STEP_PRODUCT.BUTTON.ADD_PRODUCT') }}
          </v-btn>
        </div>
      </v-card>
      <!-- END ELECTRO WORKFLOW -->

      <!-- GENERIC WORKFLOW -->
      <v-card
        v-else
        class="rounded-xl mt-8 d-flex-column global-card"
        :border="false"
        :elevation="0"
      >
        <v-form ref="formGenericCreateRepairFile" v-model="formGenericCreateRepairFile">
          <v-col md="8"
          ><h1>{{ $t('FILE_STEP_PRODUCT.CARD_GENERIC.TITLE') }}</h1></v-col
          >
          <v-col class="content-card"> {{ $t('FILE_STEP_PRODUCT.CARD_GENERIC.SUB_TITLE') }}</v-col>
          <!--FILTERS-->
          <v-row class="mt-4 pl-3">
            <!--MANUFACTURER-->
            <v-col cols="12" md="4">
              <p data-custom="true" class="mb-4">
                {{ $t('FILE_STEP_PRODUCT.SELECT.MANUFACTURER') }}
              </p>
              <v-autocomplete
                id="select-generic-manufacturer"
                v-model="selectedGenericManufacturer"
                :items="manufacturerList"
                :no-data-text="$t('COMMON.NO_DATA')"
                :placeholder="$t('FILE_STEP_PRODUCT.PLACEHOLDER.MANUFACTURER')"
                hide-details
                clearable
                item-title="name"
                item-value="technical_id"
              >
                <template v-slot:selection="{ item }">
                  <v-chip size="small">
                    <span>{{ item.title }}</span>
                  </v-chip>
                </template>
                <template v-slot:item="{ item, props }">
                  <v-list-item :title="item.title" :value="item.value" v-bind="props">
                    <v-tooltip
                      v-if="shouldShowTooltip(item, 'select-generic-manufacturer')"
                      activator="parent"
                      location="bottom left"
                    >
                      {{ item.title }}
                    </v-tooltip>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-row class="mt-10 pl-3">
            <!--PRODUCT NAME-->
            <v-col cols="12" md="4">
              <p data-custom="true">{{ $t('FILE_STEP_PRODUCT.CARD_GENERIC.INPUT.NAME') }}</p>
              <v-text-field
                v-model="productName"
                :rules="requiredRules"
                :error-messages="error?.productName"
                required
              />
            </v-col>

            <!--NETWORK CODE REF-->
            <v-col cols="12" md="4">
              <p data-custom="true">
                {{ $t('FILE_STEP_PRODUCT.CARD_GENERIC.INPUT.CODE_REF_NETWORK') }}
              </p>
              <v-text-field
                v-model="networkCodeRef"
                :rules="requiredRules"
                :error-messages="error?.networkCodeRef"
                required
              />
            </v-col>

            <!--MANUFACTURER CODE REF-->
            <v-col cols="12" md="4">
              <p data-custom="true">
                {{ $t('FILE_STEP_PRODUCT.CARD_GENERIC.INPUT.CODE_REF_MANUFACTURER') }}
              </p>
              <v-text-field
                v-model="manufacturerCodeRef"
                :rules="requiredRules"
                :error-messages="error?.manufacturerCodeRef"
                required
              />
            </v-col>
          </v-row>
          <div class="back-to-search-product">
            <button class="add-product-question mt-4" @click="setGenericProduct(false)">
              {{ $t('FILE_STEP_PRODUCT.CARD_GENERIC.BUTTON') }}
            </button>
          </div>
        </v-form>
      </v-card>
      <!-- END GENERIC WORKFLOW -->
    </v-col>
  </v-row>

  <v-col cols="12" class="d-flex justify-start custom-button">
    <v-btn
      class="pl-15 pr-15"
      :class="checkValidation() ? 'boom-button-transparent' : 'boom-button'"
      variant="outlined"
      :disabled="checkValidation()"
      @click="submit"
    >
      {{ $t('COMMON.BUTTON.CONTINUE') }}
      <img
        alt="arrow-icon"
        :class="checkValidation() ? 'arrow-black-svg' : 'arrow-white-svg'"
        src="@/assets/icons/file/arrow_small_right.svg"
      />
    </v-btn>
  </v-col>
  <AsbestosSheetModal ref="asbestosSheetModal" />
  <SelectStoreModal ref="selectStoreModal" />
</template>

<script>
import {
  capitalizeFirstLetter,
  shouldShowTooltip, handleResponseError
} from '@/components/utils/globalFunction'
import ProductService from '@/services/ProductService'
import ManufacturerService from '@/services/referentiel/ManufacturerService'
import CategoryService from '@/services/CategoryService'
import SubCategoryService from '@/services/SubCategoryService'
import AsbestosSheetModal from '@/components/modal/repair-file/AsbestosSheetModal.vue'
import SelectStoreModal from '@/components/modal/repair-file/SelectStoreModal.vue'
import RepairFileService from '@/services/RepairFileService'
import { auth as $store } from '@/store/AuthModule'
import FolderWorkflowTypeConstant from '@/constants/folderWorkflowTypeConstant'
import { VInfiniteScroll } from 'vuetify/components'
import { RepairFile } from '@/models/RepairFile'
import Validator from '@/components/utils/validator'
import RepairFileStatusDetailConstant from '@/constants/repairFileStatusDetailConstant'

export default {
  name: 'StepProductPage',
  components: {
    SelectStoreModal,
    AsbestosSheetModal,
    VInfiniteScroll
  },
  props: {
    currentStep: Number,
    currentRepairFile: {
      type: RepairFile,
      default: null
    }
  },
  data() {
    return {
      repairFile: null,
      search: '',
      awaitingSearch: false,
      sort: 'last_update',
      order: 'desc',
      page: 1,
      pages: null,
      limit: 12,
      showFilter: false,
      filters: {},
      dialogStates: [],
      addProduct: false,
      error: null,

      selectedStore: null,
      selectedRadio: null,
      file: '',

      products: [],
      selectedProduct: null,

      manufacturerList: [],
      selectedManufacturer: null,

      categoryList: [],
      selectedCategory: null,

      subCategoryList: [],
      selectedSubCategory: [],

      loadingMore: false,

      requiredRules: Validator.requiredRules,

      // GENERIC CARD VARIABLE
      formGenericCreateRepairFile: false,
      productName: '',
      networkCodeRef: '',
      manufacturerCodeRef: '',
      genericProduct: false,

      selectedGenericManufacturer: null
    }
  },
  async mounted() {
    if (!this.currentRepairFile) {
      const storeList = this.$store.getters['auth/getStoreListCollaborator']
      if (storeList.length > 1) {
        const response = await this.$refs.selectStoreModal.show({
          arrayList: storeList,
          title: this.$t('FILE_STEP_PRODUCT.STORE_MODAL.TITLE'),
          message: this.$t('FILE_STEP_PRODUCT.STORE_MODAL.MESSAGE'),
          placeHolder: this.$t('FILE_STEP_PRODUCT.STORE_MODAL.PLACEHOLDER'),
          validateButton: this.$t('COMMON.BUTTON.CONTINUE')
        })
        if (response.confirmed) {
          this.selectedStore = response.selection
          this.$store.commit('auth/setRepairFileStoreSelected', this.selectedStore)
        }
      } else {
        this.selectedStore = storeList[0]
        this.$store.commit('auth/setRepairFileStoreSelected', storeList[0])
      }
    } else {
      this.selectedStore = this.$store.getters['auth/getRepairFileStoreSelected']
    }
    await this.getProductList()
    await this.getManufacturerList()
    if (
      this.currentRepairFile &&
      this.currentRepairFile.product_folder_workflow === FolderWorkflowTypeConstant.GENERIC &&
      !this.currentRepairFile.product_boomerang_id
    ) {
      this.genericProduct = true
      await this.patchGenericData()
    }
  },
  watch: {
    async selectedManufacturer() {
      this.selectedProduct = null
      if (this.selectedManufacturer) {
        await this.getCategoryListByManufacturer()
        this.filters = { ...this.filters, manufacturer_id: this.selectedManufacturer }
      } else {
        delete this.filters.manufacturer_id
      }
      this.page = 1
      await this.getProductList()
    },
    async selectedCategory() {
      this.selectedProduct = null
      if (this.selectedCategory) {
        await this.getSubCategoryListByCategory()
        this.filters = { ...this.filters, category_id: this.selectedCategory }
      } else {
        delete this.filters.category_id
      }
      this.page = 1
      await this.getProductList()
    },
    async selectedSubCategory() {
      this.selectedProduct = null
      if (this.selectedSubCategory.length !== 0) {
        this.filters = { ...this.filters, sub_category_id: this.selectedSubCategory }
      } else {
        delete this.filters.sub_category_id
      }
      this.page = 1
      await this.getProductList()
    },
    search() {
      if (!this.awaitingSearch) {
        setTimeout(() => {
          this.selectedProduct = null
          this.getProductList()
          this.awaitingSearch = false
        }, 1000)
      }
      this.awaitingSearch = true
    }
  },
  methods: {
    shouldShowTooltip,
    capitalizeFirstLetter,
    async submit() {
      if (!this.genericProduct) {
        const product = this.products[this.selectedProduct]
        if (this.currentRepairFile && product.asbestos_sheet === 'inactive') {
          const currentProductBoomerangId = this.currentRepairFile?.product_boomerang_id
          if (currentProductBoomerangId !== product.boomerang_id) {
            await this.updateRepairFile(product)
          } else {
            this.$emit('change-step', this.currentStep + 1)
          }
        } else if (product.asbestos_sheet === 'active') {
          this.$refs.asbestosSheetModal.show().then(async (result) => {
            if (result.confirmed) {
              this.selectedRadio = result.selectedRadio
              this.file = result.file
              if (this.currentRepairFile) {
                await this.updateRepairFile(product)
              } else {
                await this.createRepairFile(product)
              }
            }
          })
          this.selectedRadio = null
          this.file = ''
        } else {
          await this.createRepairFile(product)
        }
      } else {
        if (this.currentRepairFile) {
          await this.updateGenericRepairFile()
          this.$emit('change-step', this.currentStep + 1)
        } else {
          await this.createGenericRepairFile()
        }
      }
    },
    async createRepairFile(product) {
      const formData = new FormData()
      const data = {
        product_boomerang_id: product.boomerang_id,
        store_boomerang_id: this.selectedStore.boomerang_id,
        requestor_collaborator_id: this.getCurrentCollaborator().technical_id,
        product_asbestos_answer: this.selectedRadio ? this.selectedRadio : false
      }

      formData.append('data', JSON.stringify(data))
      if (this.file && this.selectedRadio) {
        formData.append('product_asbestos_proof_file', this.file)
      }
      try {
        const repairFileResponse = await RepairFileService.create(formData)
        this.$router.push('/repair-file/' + repairFileResponse.boomerang_id)
      } catch (error) {
        handleResponseError(error)
      }
    },
    async updateRepairFile(product) {
      const formData = new FormData()
      const data = {
        status_detail: RepairFileStatusDetailConstant.PRODUCT_SELECTED,
        product_boomerang_id: product.boomerang_id,
        store_boomerang_id: this.currentRepairFile.store_boomerang_id,
        requestor_collaborator_id: this.getCurrentCollaborator().technical_id,
        product_asbestos_answer: this.selectedRadio ? this.selectedRadio : false
      }
      formData.append('data', JSON.stringify(data))
      if (this.file && this.selectedRadio) {
        formData.append('product_asbestos_proof_file', this.file)
      }
      try {
        await RepairFileService.update(this.currentRepairFile.technical_id, formData)
        this.$emit('change-step', this.currentStep + 1)
      } catch (error) {
        handleResponseError(error)
      }
    },
    async getManufacturerList() {
      try {
        const response = await ManufacturerService.getNetworkAndStoreManufacturerList(
          this.selectedStore.technical_id
        )
        this.manufacturerList = response.item_list
      } catch (error) {
        handleResponseError(error)
      }
    },
    async getCategoryListByManufacturer() {
      try {
        const manufacturerId = this.genericProduct
          ? this.selectedGenericManufacturer
          : this.selectedManufacturer
        const response = await CategoryService.getCategoryListByManufacturerId(manufacturerId)
        this.categoryList = response.item_list
      } catch (error) {
        handleResponseError(error)
      }
    },
    async getSubCategoryListByCategory() {
      try {
        const categoryId = this.selectedCategory
        const response = await SubCategoryService.getSubCategoryListByCategoryId(categoryId)
        this.subCategoryList = response.item_list
      } catch (error) {
        handleResponseError(error)
      }
    },
    load({ done }) {
      setTimeout(async () => {
        done(await this.loadMore())
      }, 250)
    },
    async onScroll(event) {
      const scrollContainer = event.target
      // Actual position
      const scrollLeft = scrollContainer.scrollLeft

      const scrollWidth = scrollContainer.scrollWidth
      // MAX SIZE
      const scrollLeftMax = scrollWidth - scrollContainer.clientWidth

      if (!this.loadingMore && scrollLeft >= (scrollLeftMax * 3) / 5) {
        this.loadingMore = true
        await this.loadMore()
        this.loadingMore = false
      }
    },
    async loadMore() {
      if (this.page < this.pages) {
        this.page = this.page + 1
        const response = await ProductService.getPaginatedNetworkAndStoreProductList(
          this.search,
          this.sort,
          this.order,
          this.page,
          this.limit,
          this.filters,
          this.selectedStore.technical_id
        )
        this.products.push(...response.data)
        this.products.forEach((product, index) => {
          if (this.selectedProduct === null) {
            if (this.currentRepairFile?.product_boomerang_id === product.boomerang_id) {
              this.selectedProduct = index
              product.selected = true
            } else {
              product.selected = false
            }
          }
        })
        return 'ok'
      } else return 'empty'
    },
    async getProductList() {
      this.loading = true
      this.page = 1
      try {
        const response = await ProductService.getPaginatedNetworkAndStoreProductList(
          this.search,
          this.sort,
          this.order,
          this.page,
          this.limit,
          this.filters,
          this.selectedStore.technical_id
        )
        this.products = response.data
        this.products.forEach((product, index) => {
          if (this.currentRepairFile?.product_boomerang_id === product.boomerang_id) {
            if (this.selectedProduct === null) {
              this.selectedProduct = index
            }
            product.selected = true
          } else {
            product.selected = false
          }
        })
        this.pages = response.max_page
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    openDialog(index) {
      if (this.dialogStates[index] === true) {
        this.$set(this.dialogStates, index, true)
      }
    },
    closeDialog(index) {
      this.dialogStates[index] = false
    },
    getCurrentCollaborator() {
      return $store.state.currentCollaborator
    },
    selectProduct(index) {
      //not reparable ===  no selectable
      if (this.products[index].reparable === 'inactive') {
        return
      }
      if (this.selectedProduct !== null) {
        this.products[this.selectedProduct].selected = false
      }
      this.products[index].selected = true
      this.selectedProduct = index
    },
    async createGenericRepairFile() {
      const data = {
        store_boomerang_id: this.selectedStore.boomerang_id,
        requestor_collaborator_id: this.getCurrentCollaborator().technical_id,
        product_name: this.productName,
        network_code_ref: this.networkCodeRef,
        manufacturer_code_ref: this.manufacturerCodeRef,
        manufacturer_id: this.selectedGenericManufacturer
      }
      try {
        const repairFileResponse = await RepairFileService.createGeneric(data)
        this.$router.push('/repair-file/' + repairFileResponse.boomerang_id)
      } catch (error) {
        handleResponseError(error)
      }
    },
    async updateGenericRepairFile() {
      const formData = new FormData()
      const data = {
        status_detail: RepairFileStatusDetailConstant.PRODUCT_SELECTED,
        store_boomerang_id: this.currentRepairFile.store_boomerang_id,
        is_generic: this.genericProduct,
        requestor_collaborator_id: this.getCurrentCollaborator().technical_id,
        product_name: this.productName,
        network_code_ref: this.networkCodeRef,
        manufacturer_code_ref: this.manufacturerCodeRef,
        manufacturer_id: this.selectedGenericManufacturer
      }
      formData.append('data', JSON.stringify(data))
      try {
        const repairFileResponse = await RepairFileService.update(
          this.currentRepairFile.technical_id,
          formData
        )
        this.$router.push('/repair-file/' + repairFileResponse.boomerang_id)
      } catch (error) {
        handleResponseError(error)
      }
    },
    checkValidation() {
      if (this.genericProduct) {
        return !this.formGenericCreateRepairFile || !this.selectedGenericManufacturer
      } else {
        return this.selectedProduct === null
      }
    },
    async patchGenericData() {
      const manufacturer = this.manufacturerList.find(
        (manufacturer) =>
          manufacturer.boomerang_id === this.currentRepairFile.manufacturer_boomerang_id
      )
      if (manufacturer) {
        this.selectedGenericManufacturer = manufacturer.technical_id
      }
      this.productName = this.currentRepairFile.product_name
      this.networkCodeRef = this.currentRepairFile.product_network_code_ref
      this.manufacturerCodeRef = this.currentRepairFile.product_manufacturer_code_ref
    },
    setGenericProduct(bool) {
      this.genericProduct = bool

      this.selectedManufacturer = null
      this.selectedCategory = null
      this.selectedSubCategory = []

      this.selectedGenericManufacturer = null
    }
  }
}
</script>
<style scoped>
.global-card {
  padding: 24px;
}

.custom-button {
  padding: 10px 0 10px 0 !important;
}

:deep(.product-card) {
  position: relative;
  box-shadow: unset;
  min-width: 250px !important;
  max-width: 250px !important;
  max-height: 350px !important;
  margin-right: 10px;
  border: 1px solid #dadada;
  padding: 10px;
  border-radius: 10px;
}

.empty-image {
  min-width: 250px !important;
  max-width: 250px !important;
}

:deep(.unrepairable-card):hover {
  position: relative;
  box-shadow: unset;
  min-width: 250px !important;
  max-width: 250px !important;
  margin-right: 10px;
  border: 1px solid #dadada;
  padding: 10px;
  border-radius: 10px;
  background: linear-gradient(to bottom, rgba(255, 255, 255) 55%, rgb(239, 239, 239) 45%);
}

:deep(.unrepairable-card) {
  position: relative;
  box-shadow: unset;
  min-width: 250px !important;
  max-width: 250px !important;
  margin-right: 10px;
  border: 1px solid #dadada;
  padding: 10px;
  border-radius: 10px;
  background: #dadada;
}

:deep(.picture) {
  height: 150px !important;
  width: 150px !important;
}

:deep(.manufacturer-name) {
  align-self: self-start;
  color: #559a1b;
}

:deep(.product-name) {
  align-self: self-start;
}

:deep(.product-description) {
  color: #727272;
  align-self: self-start;
}

.add-product-question {
  padding: unset;
  text-decoration: underline;
  color: #acacac;
  display: flex;
  justify-content: end;
}

:deep(.product-name-inactive) {
  color: #acacac;
  align-self: self-start;
}

:deep(.v-infinite-scroll__side) {
  padding: unset;
}

.picture .picture-content {
  width: 150px;
  height: 150px;
  margin: auto;
}

.picture {
  margin: auto;
  display: flex;
  width: 150px;
  height: 150px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: white;
}

.dialog-picture .dialog-picture-content {
  width: 50%;
  height: 50%;
  margin: auto;
}

.dialog-picture {
  display: flex;
  width: auto;
  height: 500px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  padding: 25%;
}

.v-infinite-scroll {
  height: 350px;
  width: 100%;
}

.text-overflow-ellipsis {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  max-width: 100%;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #ef5454;
}

.dialog-close-btn {
  position: fixed;
  top: -25px;
  right: -25px;
  height: 50px;
  width: 50px;
  background: white;
  border-radius: 50%;
}

.arrow-black-svg {
  height: 100%;
  width: 50%;
  filter: invert(0%) sepia(35%) saturate(13%) hue-rotate(154deg) brightness(96%) contrast(100%);
}

.arrow-white-svg {
  height: 100%;
  width: 50%;
  filter: invert(100%) sepia(0%) saturate(1%) hue-rotate(209deg) brightness(109%) contrast(101%);
}

.boom-button-transparent:hover .arrow-svg {
  filter: invert(48%) sepia(35%) saturate(7492%) hue-rotate(64deg) brightness(105%) contrast(79%);
}

.cross-svg {
  height: 15px;
  filter: invert(0%) sepia(35%) saturate(13%) hue-rotate(154deg) brightness(96%) contrast(100%);
}

.boom-button-transparent:hover .cross-svg {
  filter: invert(89%) sepia(98%) saturate(1%) hue-rotate(266deg) brightness(110%) contrast(97%);
}

.dialog-img {
  height: 100%;
  width: 50%;
}

.selected-card {
  border: 1px solid #559a1b;
}

.content-card {
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.back-to-search-product {
  display: flex;
  justify-content: end;
}
</style>
